import { SUPPORTED_LANGUAGES } from "./constants/supported_languages";
import { i18nState, useI18nStore } from "./store";
import { LangKey } from "./types/LangKey";
import { OnlyKeys } from "./types/OnlyKeys";
import { RecordByLocale, RecordByLocaleWithFallback } from "./types/RecordByLocale";
import { SupportedLocale } from "./types/SupportedLocale";
import { mapValuesDeep } from "./utils/map";
import { isRtlLocale } from "./constants/rtl_langs";
import React from "react";

interface UseTranslationReturn {
  /**
   * Translate a key to the current locale
   * @param key The key to translate
   * @returns The translated string
   * @example
   * t("hello")
   * // returns "مرحبا" for the Arabic locale
   * // returns "Hello" for the English locale
   */
  t: (key: LangKey) => string;

  /**
   * Get a value from a record by locale (The current locale is used as a key).
   * @param valsByLocale The record to get the value from
   * @param fallbackLocale The fallback locale
   * @returns The value
   * @example
   * byLocale({ en: "Hello", ar: "مرحبا" }, "en")
   * // returns "Hello" for the English locale
   * // returns "مرحبا" for the Arabic locale
   */
  byLocale: <T extends RecordByLocale<any> | RecordByLocaleWithFallback<any>>(
    valsByLocale: T extends RecordByLocaleWithFallback<any>
      ? OnlyKeys<RecordByLocaleWithFallback<any>, T>
      : T extends RecordByLocale<any>
        ? OnlyKeys<RecordByLocale<any>, T>
        : never,
    fallbackLocale?: T extends RecordByLocaleWithFallback<any> ? undefined : SupportedLocale,
  ) => T[keyof T];

  /**
   * Get the path to a file in the assets folder based on the locale.
   *
   * En file has no prefix. Other locals follow the format `{locale}-{fileName}`.
   * @param path The path to the file in the assets folder
   * @param fileName The name of the file
   * @returns The path to the file
   * @example
   * tFile("images", "bubble-upload-pic.png")
   * // returns "images/ar-bubble-upload-pic.png" for the Arabic locale
   * // returns "images/bubble-upload-pic.png" for the English locale
   */
  tFile: (path: string, fileName: string) => string;

  /**
   * Translate a number to the current locale
   * @param num The number to translate
   * @returns The translated number
   * @example
   * tNum(123)
   * // returns "123" for the English locale
   * // returns "١٢٣" for the Arabic locale
   */
  tNum: (num: number | string) => string;

  /**
   * Translate a deep object
   * @param obj The object to translate
   * @returns The translated object
   * @example
   * tDeep({ hello: "world" })
   * // returns { hello: "مرحبا" } for the Arabic locale
   * // returns { hello: "Hello" } for the English locale
   */
  tDeep: <T extends Record<string, any>>(obj: T) => T;

  /**
   * Get a property from an object with a camel suffix
   * @param obj The object to get the property from
   * @param prop The property to get
   * @returns The property
   * @example
   * tProp({ hello: "world" }, "hello")
   * // returns "مرحبا" for the Arabic locale
   * // returns "Hello" for the English locale
   */
  tProp: <T extends Record<string, any>, KeyT extends keyof T>(obj: T, prop: KeyT) => T[KeyT];

  /**
   * Initialize the translation by loading the dictionary for the current locale
   */
  initTranslation: () => void;

  /**
   * Change the current locale
   * @param locale The locale to change to
   */
  changeLocale: (locale: SupportedLocale) => void;

  /**
   * Switch the language
   *
   */
  switchLanguage: () => void;

  /**
   * The current locale
   */
  locale: SupportedLocale;

  /**
   * The details of the current locale
   */
  localeDetails: (typeof SUPPORTED_LANGUAGES)[SupportedLocale];

  /**
   * Whether the current locale is Arabic
   */
  isAr: boolean;

  /**
   * Whether the current locale is RTL
   */
  isRtl: boolean;

  /**
   * The direction of the current locale
   */
  dir: "rtl" | "ltr";

  /**
   * Get the dictionaries for the current locale
   */
  getDictionaries: () => i18nState["dictionaries"];
}

export function useTranslation(): UseTranslationReturn {
  const { locale, dictionaries, loadDictionary, setLocale, t, tNum } = useI18nStore();

  const getDictionaries = React.useCallback(() => dictionaries, [dictionaries]);

  const localeDetails = React.useMemo(() => SUPPORTED_LANGUAGES[locale], [locale]);

  const initTranslation = React.useCallback(() => {
    if (!dictionaries[locale]) {
      setLocale(locale);
    }
  }, [loadDictionary, locale]);

  const tDeep = React.useCallback(
    <T extends Record<string, any>>(obj: T) => {
      return mapValuesDeep(obj, (value) => {
        if (typeof value === "string") {
          return t(value as any);
        }

        return value;
      });
    },
    [t],
  );

  const changeLocale = React.useCallback(
    (locale: SupportedLocale) => {
      setLocale(locale);
    },
    [setLocale],
  );

  const isRtl = React.useMemo(() => {
    return isRtlLocale(locale);
  }, [locale]);

  const dir = React.useMemo(() => {
    const res = isRtl ? "rtl" : "ltr";
    document.dir = res;
    document.documentElement.classList.add(res);
    document.documentElement.classList.remove(isRtl ? "ltr" : "rtl");
    return res;
  }, [isRtl]);

  const isAr = React.useMemo(() => locale.startsWith("ar"), [locale]);

  const byLocale = React.useCallback(
    function <T extends RecordByLocale<any> | RecordByLocaleWithFallback<any>>(
      valsByLocale: T extends RecordByLocaleWithFallback<any>
        ? OnlyKeys<RecordByLocaleWithFallback<any>, T>
        : T extends RecordByLocale<any>
          ? OnlyKeys<RecordByLocale<any>, T>
          : never,
      fallbackLocale?: T extends RecordByLocaleWithFallback<any> ? undefined : SupportedLocale,
    ): T[keyof T] {
      const val = valsByLocale[locale];

      if (val !== undefined) {
        return val;
      }

      if ("fallback" in valsByLocale) {
        return valsByLocale.fallback;
      } else if (fallbackLocale) {
        return valsByLocale[fallbackLocale];
      }

      throw new Error("ByLocale expected fallback");
    },
    [locale],
  );

  const tFile = React.useCallback(
    (path: string, fileName: string) => {
      return `${path}/${byLocale({
        en: "",
        fallback: `${locale}-`,
      })}${fileName}`;
    },
    [locale, byLocale],
  );

  const camelSuffix = React.useMemo(() => {
    return byLocale({
      en: "",
      fallback: `${locale.charAt(0)?.toUpperCase()}${locale.charAt(1)}`,
    }) as string;
  }, [locale]);

  const tProp = React.useCallback(
    <T extends Record<string, any>, KeyT extends keyof T>(obj: T, prop: KeyT) => {
      return (obj[`${prop as string}${camelSuffix}`] as T[KeyT]) ?? obj[prop];
    },
    [locale],
  );

  const switchLanguage = React.useCallback(() => {
    if (isRtl) changeLocale("en");
    else changeLocale("ar");
  }, [isRtl, changeLocale]);

  return {
    t,
    tFile,
    tNum,
    tDeep,
    tProp,
    getDictionaries,
    initTranslation,
    changeLocale,
    byLocale,
    switchLanguage,
    locale,
    localeDetails,
    isAr,
    isRtl,
    dir,
  };
}

export default useTranslation;
